import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useApi, img } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal, ZoomWrap } from "@/components/site/Reveal";
import { ListingSlider } from "@/components/site/ListingSlider";
import { SocialFeed } from "@/components/site/SocialFeed";
import { InquiryForm } from "@/components/site/InquiryForm";
import { StoryCard } from "@/components/site/StoryCard";
import { HeroSlideshow } from "@/components/site/HeroSlideshow";
import { SearchBand } from "@/components/site/SearchBar";
import { MeetBlue } from "@/components/site/MeetBlue";
import { CortezHighlight } from "@/components/site/CortezHighlight";
import { UpcomingRetreats } from "@/components/site/UpcomingRetreats";
import { StablesSpotlight } from "@/components/site/StablesSpotlight";
import { useLang } from "@/lib/i18n";

const PILLARS = [
  { to: "/directory", image: "/media/home-play.jpg", span: "md:col-span-7 md:row-span-2", id: "play" },
  { to: "/for-rent", image: "/media/home-stay.jpg", span: "md:col-span-5", id: "stay" },
  { to: "/homes-and-land-for-sale", image: "/media/home-homes.jpg", span: "md:col-span-5", id: "homes" },
  { to: "/homes-and-land-for-sale?category=land", image: "/media/home-land.jpg", span: "md:col-span-12", id: "land" },
];

const Pillars = () => {
  const { t, to } = useLang();
  const texts = t("home.pillars");
  return (
  <section className="container-x py-24 sm:py-32" data-testid="home-pillars">
    <Reveal className="max-w-3xl">
      <p className="eyebrow">{t("home.pillarsEyebrow")}</p>
      <h2 className="mt-3 text-4xl font-black tracking-tight text-forest sm:text-5xl">{t("home.pillarsTitle")}</h2>
    </Reveal>
    <div className="mt-14 grid gap-6 md:auto-rows-[260px] md:grid-cols-12">
      {PILLARS.map((p, i) => (
        <Reveal key={p.id} delay={i * 0.08} className={`${p.span} h-[320px] md:h-auto`}>
          <Link to={to(p.to)} className="img-zoom group relative block h-full overflow-hidden rounded-2xl" data-testid={`pillar-${p.id}`}>
            <ZoomWrap className="absolute inset-0"><img src={p.image} alt={texts[i][0]} loading="lazy" className="absolute inset-0 h-full w-full object-cover" /></ZoomWrap>
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-6 p-7">
              <div>
                <h3 className="font-display text-2xl font-black text-white sm:text-3xl">{texts[i][0]}</h3>
                <p className="mt-2 max-w-md text-sm font-medium text-white/85">{texts[i][1]}</p>
              </div>
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-forest transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
  );
};

const Featured = ({ title, eyebrow, items, to, cta, testId, aside }) => {
  const { to: lto } = useLang();
  return (
  <section className="container-x py-20 sm:py-24" data-testid={testId}>
    <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-3 text-4xl font-black tracking-tight text-forest sm:text-5xl">{title}</h2>
      </div>
      <Link to={lto(to)} className="btn-outline" data-testid={`${testId}-view-all`}>{cta} <ArrowRight className="h-4 w-4" /></Link>
    </Reveal>
    {aside ? (
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8"><ListingSlider items={items} testId={testId} compact /></div>
        <Reveal delay={0.1} className="lg:col-span-4 lg:mt-12">{aside}</Reveal>
      </div>
    ) : <ListingSlider items={items} testId={testId} />}
  </section>
  );
};

const StoriesPreview = ({ posts }) => {
  const { t, to } = useLang();
  if (!posts.length) return null;
  return (
    <section className="container-x py-24 sm:py-28" data-testid="home-stories">
      <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow">{t("home.storiesEyebrow")}</p>
          <h2 className="mt-3 text-4xl font-black tracking-tight text-forest sm:text-5xl">{t("home.storiesTitle")}</h2>
        </div>
        <Link to={to("/stories")} className="btn-outline" data-testid="home-stories-view-all">{t("home.storiesCta")} <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
      <div className="mt-12 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        {posts.slice(0, 4).map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.08}><StoryCard post={p} /></Reveal>
        ))}
      </div>
    </section>
  );
};

const DirSlide = ({ d }) => {
  const { L, to } = useLang();
  return (
    <Link to={to(`/directory#dir-${d.id}`)} className="group block" data-testid={`lifestyle-dir-${d.id}`}>
      <div className="img-zoom overflow-hidden rounded-2xl"><img src={img(d.image)} alt={`${L(d, "name")} — Escamequita`} loading="lazy" className="aspect-[4/3] w-full object-cover" /></div>
      <p className="eyebrow mt-5">{L(d, "category")}</p>
      <h3 className="mt-1 flex items-center gap-2 font-display text-xl font-black text-ink transition-colors duration-200 group-hover:text-forest">{L(d, "name")} <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100" /></h3>
    </Link>
  );
};

const Lifestyle = () => {
  const { t, to } = useLang();
  const { data } = useApi("/directory", []);
  return (
  <section className="border-y border-clay bg-white py-24 sm:py-32" data-testid="home-lifestyle">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">{t("home.lifeEyebrow")}</p>
        <h2 className="mt-3 text-4xl font-black tracking-tight text-forest sm:text-5xl">{t("home.lifeTitle")}</h2>
        <p className="mt-6 text-base font-medium leading-relaxed text-moss md:text-lg">{t("home.lifeText")}</p>
        <Link to={to("/directory")} className="btn-primary mt-8" data-testid="lifestyle-directory-cta">{t("home.lifeCta")}</Link>
      </Reveal>
      <Reveal delay={0.1} className="min-w-0 lg:col-span-8">
        <div className="-mt-12"><ListingSlider items={data} testId="lifestyle-slider" seconds={4} compact renderItem={(d) => <DirSlide d={d} />} /></div>
      </Reveal>
    </div>
  </section>
  );
};

const ContactBand = ({ s }) => {
  const { t, L } = useLang();
  return (
  <section className="container-x py-24 sm:py-32" data-testid="home-contact">
    <div className="grid gap-12 rounded-3xl border border-clay bg-linen p-8 sm:p-12 lg:grid-cols-2 lg:p-16">
      <Reveal>
        <p className="eyebrow">{t("home.contactEyebrow")}</p>
        <h2 className="mt-3 text-4xl font-black tracking-tight text-forest sm:text-5xl">{t("home.contactTitle")}</h2>
        <p className="mt-5 text-base font-medium text-moss md:text-lg">{t("home.contactText", { name: s?.contact_name || "Blue" })}</p>
        <div className="mt-8 space-y-2 text-base font-bold text-ink">
          <p data-testid="contact-email">{s?.contact_email}</p>
          <p data-testid="contact-phone">{t("common.telWa")}: {s?.contact_phone_display}</p>
          <p className="text-sm font-semibold text-moss">{L(s, "broker_license")}</p>
        </div>
      </Reveal>
      <Reveal delay={0.1}><InquiryForm /></Reveal>
    </div>
  </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const { data: s } = useApi("/settings");
  const { data: sale } = useApi("/listings?kind=sale", []);
  const { data: rent } = useApi("/listings?kind=rent", []);
  const { data: posts } = useApi("/posts?limit=4", []);
  return (
    <>
      <Seo
        title={t("home.seoTitle")}
        description={t("home.seoDesc")}
        jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "Escamequita", url: "https://www.escamequita.com", sameAs: ["https://www.facebook.com/Escamequita", "https://www.instagram.com/escamequita"] }}
      />
      <HeroSlideshow s={s} />
      <SearchBand />
      <Pillars />
      <Featured testId="home-for-sale" eyebrow={t("home.saleEyebrow")} title={t("home.saleTitle")} items={sale} to="/homes-and-land-for-sale" cta={t("home.saleCta")} aside={<CortezHighlight />} />
      <MeetBlue photo={s?.about_photo} />
      <Featured testId="home-stays" eyebrow={t("home.stayEyebrow")} title={t("home.stayTitle")} items={rent} to="/for-rent" cta={t("home.stayCta")} />
      <Lifestyle />
      <StablesSpotlight />
      <StoriesPreview posts={posts} />
      <SocialFeed />
      <UpcomingRetreats />
      <ContactBand s={s} />
    </>
  );
}
